import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Copy, BadgeCheck, Clock, AlertCircle, Loader2, Link as LinkIcon, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function SalesRepDashboardPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.role !== "sales_rep") {
      navigate("/");
      return;
    }
    api
      .get("/sales-rep/me")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load dashboard"));
  }, [loading, user, navigate]);

  if (loading || !data) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24 text-center text-gray-500" data-testid="rep-dashboard-loading">
        {err ? <span className="text-red-600">{err}</span> : <Loader2 className="w-6 h-6 animate-spin inline" />}
      </div>
    );
  }

  const { rep, referral_link, commission_rate_percent, eligible_categories, stats, commissions } = data;
  const pending = rep.rep_status === "pending";
  const rejected = rep.rep_status === "rejected";
  const approved = rep.rep_status === "approved";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referral_link);
      toast.success("Link copied!");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-12" data-testid="rep-dashboard">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="ipv-eyebrow text-[#0A1C40]">Sales Rep Dashboard</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2" data-testid="rep-dashboard-greeting">
            Welcome, {rep.name?.split(" ")[0] || "rep"}
          </h1>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} className="text-sm text-gray-500 hover:text-black inline-flex items-center gap-1" data-testid="rep-logout-btn">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      {/* Status banner */}
      {pending && (
        <div className="mt-6 border-2 border-[#FFC220] bg-yellow-50 p-5 flex gap-3" data-testid="rep-status-pending">
          <Clock className="w-5 h-5 text-[#B45309] mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-bold text-[#7A4F00]">Application pending review</div>
            <div className="text-sm text-gray-700 mt-1">
              An admin is reviewing your application. You'll be approved within 24 hours
              and you'll get your unique referral link here. Sit tight!
            </div>
          </div>
        </div>
      )}
      {rejected && (
        <div className="mt-6 border-2 border-red-500 bg-red-50 p-5 flex gap-3" data-testid="rep-status-rejected">
          <AlertCircle className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-bold text-red-700">Application not approved</div>
            <div className="text-sm text-gray-700 mt-1">
              Your sales-rep application wasn't approved this time. Reach out to us at
              +1 (616) 410-0158 if you have questions.
            </div>
          </div>
        </div>
      )}

      {approved && (
        <>
          {/* Referral link card */}
          <div className="mt-6 border-2 border-[#0A1C40] bg-[#0A1C40] text-white p-6 lg:p-8" data-testid="rep-referral-card">
            <div className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-[#FFC220]" />
              <span className="font-bold tracking-widest uppercase text-[10px]">YOUR REFERRAL LINK</span>
            </div>
            <div className="mt-4 flex flex-col md:flex-row gap-3 md:items-center">
              <div className="font-mono text-base md:text-lg break-all bg-white text-[#0A1C40] px-4 py-3 flex-1" data-testid="rep-referral-link-display">
                {referral_link}
              </div>
              <button
                onClick={copyLink}
                className="bg-[#FFC220] text-[#0A1C40] font-bold px-5 py-3 inline-flex items-center justify-center gap-2 hover:bg-[#FFD24D]"
                data-testid="rep-copy-link-btn"
              >
                <Copy className="w-4 h-4" /> Copy
              </button>
            </div>
            <div className="mt-4 text-sm text-white/80">
              Share this link anywhere — when someone clicks it and buys{" "}
              <span className="text-[#FFC220] font-bold">{eligible_categories.join(" or ")}</span>,
              you earn a flat <span className="text-[#FFC220] font-bold">{commission_rate_percent}%</span>.
            </div>
          </div>

          {/* Stats */}
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total earned" value={fmtMoney(stats.total_earned)} testid="rep-stat-earned" />
            <StatCard label="Pending payout" value={fmtMoney(stats.total_pending)} testid="rep-stat-pending" />
            <StatCard label="Paid out" value={fmtMoney(stats.total_paid_out)} testid="rep-stat-paid" />
            <StatCard label="Orders referred" value={stats.order_count} testid="rep-stat-orders" />
          </div>

          {/* Commission ledger */}
          <div className="mt-10">
            <div className="ipv-eyebrow mb-4">Your commissions</div>
            {commissions.length === 0 ? (
              <div className="border border-dashed border-gray-300 p-10 text-center text-gray-500" data-testid="rep-no-commissions">
                No commissions yet. Share your link and start earning!
              </div>
            ) : (
              <div className="border border-gray-200 overflow-x-auto" data-testid="rep-commissions-table">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-4 py-3 font-bold">Date</th>
                      <th className="px-4 py-3 font-bold">Order #</th>
                      <th className="px-4 py-3 font-bold">Eligible items</th>
                      <th className="px-4 py-3 font-bold text-right">Eligible total</th>
                      <th className="px-4 py-3 font-bold text-right">Your 10%</th>
                      <th className="px-4 py-3 font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((c) => (
                      <tr key={c.id} className="border-t border-gray-100" data-testid={`rep-commission-row-${c.id}`}>
                        <td className="px-4 py-3">{(c.created_at || "").slice(0, 10)}</td>
                        <td className="px-4 py-3 font-mono text-xs">{c.order_number}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {(c.eligible_items || []).map((it) => `${it.quantity}x ${it.name}`).join(", ")}
                        </td>
                        <td className="px-4 py-3 text-right">{fmtMoney(c.eligible_subtotal)}</td>
                        <td className="px-4 py-3 text-right font-bold text-green-700">{fmtMoney(c.commission_amount)}</td>
                        <td className="px-4 py-3">
                          {c.paid_out ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 font-bold">PAID</span>
                          ) : (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 font-bold">PENDING</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-gray-200 p-5" data-testid={testid}>
      <div className="text-[10px] tracking-widest uppercase text-gray-500 font-bold">{label}</div>
      <div className="font-display font-black text-2xl mt-2">{value}</div>
    </div>
  );
}
